/**
 * bz-collection.js: behaviour for sections/bz-collection.liquid.
 *
 * Spice-lane + species browsing uses smart collection links, while the Spice picker filters cards in-page.
 * Shopify sort options navigate with ?sort_by=, and the spice-level organizer reorders the rendered grid locally. So this file is small; it only:
 *   - drives the Buddy / Habitat builder panel (the bz-habitat-zone collection page)
 *   - opens/closes the sort, spice, and species dropdown menus
 *   - keeps sold-out tiles at the bottom after local grid reordering
 *   - restores scroll position after a sort navigation
 *
 * Loaded via <script src="{{ 'bz-collection.js' | asset_url }}" defer> at the bottom of the section.
 */
(function () {
  var scope = document;
  var grid = scope.querySelector('[data-bz-grid]');

  /* ---------- Buddy / Habitat builder ------------------------------------------------------- */
  var buddyBuilder = scope.querySelector('[data-bz-buddy-builder]');
  if (buddyBuilder) {
    var buddyOptions = buddyBuilder.querySelectorAll('[data-buddy-option]');
    var buddyModel = buddyBuilder.querySelector('[data-buddy-model]');
    var buddyBadge = buddyBuilder.querySelector('[data-buddy-badge]');
    var buddyAnimal = buddyBuilder.querySelector('[data-buddy-animal]');
    var buddyKit = buddyBuilder.querySelector('[data-buddy-kit]');
    var buddyCopy = buddyBuilder.querySelector('[data-buddy-copy]');
    var buddyBehavior = buddyBuilder.querySelector('[data-buddy-behavior]');
    var buddyCore = buddyBuilder.querySelector('[data-buddy-core]');
    var buddyClose = buddyBuilder.querySelector('[data-buddy-close]');
    var buddyAnimalLink = buddyBuilder.querySelector('[data-buddy-animal-link]');
    var buddyKitLink = buddyBuilder.querySelector('[data-buddy-kit-link]');
    var buddyKitAdd = buddyBuilder.querySelector('[data-buddy-kit-add]');

    function setBuddyMatch(option) {
      var kitKey = option.getAttribute('data-kit-key') || 'bark';
      var accent = option.getAttribute('data-accent') || '#ef3b2d';
      var kitName = option.getAttribute('data-kit') || 'Buddy Kit';
      var animalName = option.getAttribute('data-animal') || 'This fella';
      var kitVariant = option.getAttribute('data-kit-variant') || '';
      var kitHandle = option.getAttribute('data-kit-handle') || '';
      var kitAvailable = option.getAttribute('data-kit-available') === 'true';

      buddyBuilder.style.setProperty('--builder-accent', accent);
      buddyBuilder.setAttribute('data-kit-key', kitKey);

      for (var i = 0; i < buddyOptions.length; i++) {
        buddyOptions[i].classList.toggle('is-active', buddyOptions[i] === option);
        buddyOptions[i].setAttribute('aria-selected', buddyOptions[i] === option ? 'true' : 'false');
      }
      if (buddyModel && buddyModel.tagName === 'IMG') {
        var kitImage = option.getAttribute('data-kit-image');
        if (kitImage) buddyModel.src = kitImage;
      }
      if (buddyBadge) buddyBadge.textContent = option.getAttribute('data-badge') || 'Matched fit';
      if (buddyAnimal) buddyAnimal.textContent = animalName;
      if (buddyKit) buddyKit.textContent = kitName;
      if (buddyCopy) buddyCopy.textContent = option.getAttribute('data-copy') || '';
      if (buddyBehavior) buddyBehavior.textContent = option.getAttribute('data-behavior') || '';
      if (buddyCore) buddyCore.textContent = option.getAttribute('data-core') || '';
      if (buddyClose) buddyClose.textContent = option.getAttribute('data-close') || '';
      if (buddyAnimalLink) buddyAnimalLink.href = option.getAttribute('data-animal-url') || buddyAnimalLink.href;
      if (buddyKitLink) {
        buddyKitLink.href = option.getAttribute('data-kit-url') || buddyKitLink.href;
        buddyKitLink.textContent = 'View ' + kitName;
      }
      if (buddyKitAdd) {
        buddyKitAdd.setAttribute('data-bz-variant-id', kitVariant);
        if (kitHandle) buddyKitAdd.setAttribute('data-bz-product-handle', kitHandle);
        buddyKitAdd.removeAttribute('data-prev-html');
        buddyKitAdd.innerHTML = 'Add ' + kitName;
        buddyKitAdd.disabled = !kitVariant || !kitAvailable;
      }
    }

    for (var bo = 0; bo < buddyOptions.length; bo++) {
      (function (option) {
        option.addEventListener('click', function () { setBuddyMatch(option); });
      })(buddyOptions[bo]);
    }
  }

  /* ---------- Restore scroll after a sort navigation ---------------------------------------- */
  try {
    var savedScroll = sessionStorage.getItem('bzCollScroll');
    if (savedScroll !== null) {
      sessionStorage.removeItem('bzCollScroll');
      var sy = parseInt(savedScroll, 10);
      if (!isNaN(sy) && sy > 0) {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        window.requestAnimationFrame(function () { window.scrollTo(0, sy); });
      }
    }
  } catch (_) { }

  /* ---------- Dropdown menu helper (sort + species + spice menus) --------------------------- */
  var sortBarEl = scope.querySelector('[data-bz-sort-bar]');
  function markPanelOpen(open) {
    if (!sortBarEl) return;
    if (open) {
      sortBarEl.classList.add('is-panel-open');
    } else if (!sortBarEl.querySelector('[data-bz-sort-panel]:not([hidden]), [data-bz-species-panel]:not([hidden]), [data-bz-lane-panel]:not([hidden])')) {
      sortBarEl.classList.remove('is-panel-open');
    }
  }

  // Wire a trigger + panel into an accessible popup. `onSelect(opt, close)` runs when an option is
  // chosen; pass null when the options are plain links (the browser handles the navigation).
  function wireMenu(wrap, triggerSel, panelSel, optSel, onSelect) {
    if (!wrap) return;
    var trigger = wrap.querySelector(triggerSel);
    var panel = wrap.querySelector(panelSel);
    if (!trigger || !panel) return;
    function open() { panel.hidden = false; trigger.setAttribute('aria-expanded', 'true'); markPanelOpen(true); }
    function close() { panel.hidden = true; trigger.setAttribute('aria-expanded', 'false'); markPanelOpen(false); }
    trigger.addEventListener('click', function (e) { e.stopPropagation(); if (panel.hidden) open(); else close(); });
    document.addEventListener('click', function (e) { if (!panel.hidden && !wrap.contains(e.target)) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) { close(); trigger.focus(); } });
    if (onSelect) {
      var opts = wrap.querySelectorAll(optSel);
      for (var i = 0; i < opts.length; i++) {
        (function (opt) {
          opt.addEventListener('click', function (e) {
            e.stopPropagation();
            if (opt.disabled || opt.getAttribute('aria-disabled') === 'true') return;
            onSelect(opt, close);
          });
        })(opts[i]);
      }
    }
  }

  function intAttr(el, attr, fallback) {
    var n = parseInt((el && el.getAttribute(attr)) || '', 10);
    return isNaN(n) ? fallback : n;
  }

  function sortOptionFor(mode) {
    try {
      return scope.querySelector('[data-bz-sort-wrap] [data-bz-local-sort="' + mode + '"]');
    } catch (_) {
      return null;
    }
  }

  function setSortCurrent(label) {
    var current = scope.querySelector('[data-bz-sort-current]');
    if (current && label) current.textContent = label;
  }

  function markSortSelected(selected) {
    var wrap = scope.querySelector('[data-bz-sort-wrap]');
    if (!wrap) return;
    var opts = wrap.querySelectorAll('.bz-sort__opt[data-sort], .bz-sort__opt[data-bz-local-sort]');
    for (var i = 0; i < opts.length; i++) opts[i].removeAttribute('aria-selected');
    if (selected) selected.setAttribute('aria-selected', 'true');
  }

  function setLocalSortParam(mode) {
    try {
      var url = new URL(window.location.href);
      if (mode) url.searchParams.set('bz_sort', mode);
      else url.searchParams.delete('bz_sort');
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (_) { }
  }

  function isTile(el) {
    return el && el.matches && (el.matches('.bz-tile') || el.matches('.lzz-tile'));
  }

  function gridTiles() {
    if (!grid) return [];
    return Array.prototype.filter.call(grid.children, isTile);
  }

  /* ---------- Push sold-out tiles to bottom of grid ----------------------------------------- */
  // True once the shopper has picked a sort of their own. The Liquid stops grouping sold-out
  // tiles last in that case so ?sort_by= means what it says, and this client-side pass has to
  // stand down for the same reason. Without this guard it would silently re-group the tiles the
  // server just emitted in price order, putting the broken-looking sort straight back.
  function hasExplicitSort() {
    try {
      return !!new URL(window.location.href).searchParams.get('sort_by');
    } catch (_) {
      return /[?&]sort_by=/.test(window.location.search || '');
    }
  }

  function pushSoldOutToBottom() {
    if (!grid || hasExplicitSort()) return;
    var pager = grid.querySelector('.bz-pager');
    var soldOut = [];
    var seenSoldOut = false;
    var needsMove = false;
    var children = grid.children;
    for (var i = 0; i < children.length; i++) {
      var el = children[i];
      if (isTile(el) && el.getAttribute('data-stock') === 'out') {
        seenSoldOut = true;
        soldOut.push(el);
      } else if (isTile(el) && seenSoldOut) {
        needsMove = true;
      }
    }
    if (!soldOut.length || !needsMove) return;
    for (var s = 0; s < soldOut.length; s++) {
      if (pager) grid.insertBefore(soldOut[s], pager);
      else grid.appendChild(soldOut[s]);
    }
  }

  var isReorderingSoldOut = false;
  function pushSoldOutToBottomAsync() {
    if (isReorderingSoldOut) return;
    isReorderingSoldOut = true;
    window.requestAnimationFrame(function () {
      pushSoldOutToBottom();
      isReorderingSoldOut = false;
    });
  }

  function applySpiceSort(mode, selectedOpt, updateUrl) {
    if (!grid || (mode !== 'spice-ascending' && mode !== 'spice-descending')) return false;

    var tiles = gridTiles();
    if (tiles.length < 2) return false;

    for (var i = 0; i < tiles.length; i++) {
      if (!tiles[i].hasAttribute('data-bz-original-order')) {
        tiles[i].setAttribute('data-bz-original-order', String(i));
      }
    }

    tiles.sort(function (a, b) {
      var aSpice = intAttr(a, 'data-spice', 0);
      var bSpice = intAttr(b, 'data-spice', 0);
      var aKnown = aSpice > 0;
      var bKnown = bSpice > 0;
      if (aKnown !== bKnown) return aKnown ? -1 : 1;
      if (aSpice !== bSpice) {
        return mode === 'spice-ascending' ? aSpice - bSpice : bSpice - aSpice;
      }
      return intAttr(a, 'data-bz-original-order', 0) - intAttr(b, 'data-bz-original-order', 0);
    });

    var pager = grid.querySelector('.bz-pager');
    for (var t = 0; t < tiles.length; t++) {
      if (pager) grid.insertBefore(tiles[t], pager);
      else grid.appendChild(tiles[t]);
    }

    pushSoldOutToBottom();

    selectedOpt = selectedOpt || sortOptionFor(mode);
    grid.setAttribute('data-bz-local-sort', mode);
    setSortCurrent((selectedOpt && selectedOpt.getAttribute('data-label')) || (mode === 'spice-ascending' ? 'Spice 1 -> Spice 5' : 'Spice 5 -> Spice 1'));
    markSortSelected(selectedOpt);
    if (updateUrl) setLocalSortParam(mode);
    return true;
  }

  /* ---------- Spice multi-filter ------------------------------------------------------------ */
  var activeSpiceLevels = [];

  function normalizeSpiceLevels(levels) {
    var out = [];
    for (var i = 0; i < levels.length; i++) {
      var level = String(levels[i]);
      if (level >= '1' && level <= '5' && out.indexOf(level) === -1) out.push(level);
    }
    out.sort();
    return out.length === 5 ? [] : out;
  }

  function parseSpiceLevelsParam() {
    try {
      var raw = new URL(window.location.href).searchParams.get('bz_spice') || '';
      return normalizeSpiceLevels(raw.split(','));
    } catch (_) {
      return [];
    }
  }

  function setSpiceLevelsParam(levels) {
    try {
      var url = new URL(window.location.href);
      if (levels.length) url.searchParams.set('bz_spice', levels.join(','));
      else url.searchParams.delete('bz_spice');
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (_) { }
  }

  function spiceLevelsLabel(levels, fallback) {
    if (!levels.length) return fallback || 'All spice';
    var first = parseInt(levels[0], 10);
    var last = parseInt(levels[levels.length - 1], 10);
    var contiguous = levels.length > 1 && last - first + 1 === levels.length;
    if (contiguous) return 'Spice ' + first + '-' + last;
    return 'Spice ' + levels.join(' + ');
  }

  function syncPagerSpiceParam(levels) {
    if (!grid) return;
    var links = grid.querySelectorAll('.bz-pager a');
    for (var i = 0; i < links.length; i++) {
      try {
        var url = new URL(links[i].href, window.location.href);
        if (levels.length) url.searchParams.set('bz_spice', levels.join(','));
        else url.searchParams.delete('bz_spice');
        links[i].href = url.toString();
      } catch (_) { }
    }
  }

  // Count what is actually rendered on this page, per spice level, and write those numbers into
  // the menu. The Liquid counts these replace came from a loop Shopify caps at 50 products, so
  // on a 98 product lane they were simply wrong: Spice 4 rendered "(1)" against 11 real products,
  // and the five counts summed to exactly 50 instead of 98. This control narrows the rendered
  // page, so a page-scoped number is the honest one and it always matches what selecting it does.
  /* LIVE AGAIN: this block was dormant while the spice control was missing from
     bz-collection-core.liquid. The reasoning recorded at the time was that the lane pill row above
     the toolbar already covered the same dimension server-side -- but that row is a prev/next lane
     walker, not a spice picker, so on /collections/spice-lane it offered nothing of the sort and
     the page had no way to browse by spice at all. The toolbar now renders a Spice chip row
     ([data-bz-spice-filter-wrap]) and everything below drives it. The null guards stay: the chips
     are gated on show_spice_row, so on the dry-goods lanes this still finds nothing and returns. */
  function updateSpiceCounts() {
    var wrap = scope.querySelector('[data-bz-spice-filter-wrap]');
    if (!wrap || !grid) return;
    var tiles = gridTiles();
    var byLevel = {};
    for (var i = 0; i < tiles.length; i++) {
      var s = String(intAttr(tiles[i], 'data-spice', 0));
      byLevel[s] = (byLevel[s] || 0) + 1;
    }
    var slots = wrap.querySelectorAll('[data-bz-spice-count]');
    for (var k = 0; k < slots.length; k++) {
      var lvl = slots[k].getAttribute('data-bz-spice-count');
      if (lvl === 'all') { slots[k].textContent = String(tiles.length); continue; }
      var n = byLevel[lvl] || 0;
      slots[k].textContent = String(n);
      // .bz-spice-chip is the toolbar chip; .bz-sort__opt is kept so the older dropdown markup
      // would still be handled if it is ever reinstated.
      var opt = slots[k].closest ? slots[k].closest('.bz-spice-chip, .bz-sort__opt') : null;
      if (!opt) continue;
      if (n === 0) {
        opt.classList.add('is-empty');
        opt.setAttribute('disabled', 'disabled');
        opt.setAttribute('aria-disabled', 'true');
      } else {
        opt.classList.remove('is-empty');
        opt.removeAttribute('disabled');
        opt.removeAttribute('aria-disabled');
      }
    }
  }

  function applySpiceFilter(levels, updateUrl) {
    var wrap = scope.querySelector('[data-bz-spice-filter-wrap]');
    if (!wrap || !grid) return false;
    activeSpiceLevels = normalizeSpiceLevels(levels);

    var tiles = gridTiles();
    var visible = 0;
    for (var i = 0; i < tiles.length; i++) {
      var spice = String(intAttr(tiles[i], 'data-spice', 0));
      var show = !activeSpiceLevels.length || activeSpiceLevels.indexOf(spice) !== -1;
      // Setting `hidden` is enough now that .bz-tile[hidden] / .lzz-tile[hidden] exist in
      // bz-site.css. It also takes the tile out of the accessibility tree on its own. The
      // old aria-hidden juggling here was an active defect: because the hidden attribute was
      // inert, it marked tiles aria-hidden that a sighted shopper could still see, so screen
      // readers lost products that were plainly on the page.
      tiles[i].hidden = !show;
      if (show) visible += 1;
    }

    var defaultLabel = wrap.getAttribute('data-bz-spice-filter-default') || 'All spice';
    var current = wrap.querySelector('[data-bz-spice-filter-current]');
    if (current) current.textContent = spiceLevelsLabel(activeSpiceLevels, defaultLabel);

    // The chips are plain toggle buttons, so aria-pressed is the correct state attribute for
    // them; aria-selected is only valid on option-style markup (role option/tab), so it is set
    // just for the older dropdown markup this originally drove. Putting it on a <button> fails
    // the aria-allowed-attr accessibility audit (Lighthouse, 2026-10-21 proof run).
    var allOpt = wrap.querySelector('[data-bz-spice-filter-all]');
    if (allOpt) {
      var allOn = activeSpiceLevels.length ? 'false' : 'true';
      if (allOpt.tagName !== 'BUTTON') allOpt.setAttribute('aria-selected', allOn);
      allOpt.setAttribute('aria-pressed', allOn);
    }
    var opts = wrap.querySelectorAll('[data-bz-spice-filter]');
    for (var o = 0; o < opts.length; o++) {
      var level = opts[o].getAttribute('data-bz-spice-filter');
      var on = activeSpiceLevels.indexOf(level) !== -1 ? 'true' : 'false';
      if (opts[o].tagName !== 'BUTTON') opts[o].setAttribute('aria-selected', on);
      opts[o].setAttribute('aria-pressed', on);
    }

    var count = scope.querySelector('[data-bz-sort-count]');
    if (count) {
      var total = count.getAttribute('data-bz-sort-count-total') || String(visible);
      var defaultSuffix = count.getAttribute('data-bz-sort-count-default-suffix') || 'fellas in this lane';
      var filterSuffix = count.getAttribute('data-bz-sort-count-filter-suffix') || 'fellas in selected spice';
      count.textContent = activeSpiceLevels.length ? (visible + ' ' + filterSuffix) : (total + ' ' + defaultSuffix);
    }

    var empty = scope.querySelector('[data-bz-spice-empty]');
    if (empty) empty.hidden = visible !== 0;
    if (activeSpiceLevels.length) grid.setAttribute('data-bz-spice-filter-active', activeSpiceLevels.join(','));
    else grid.removeAttribute('data-bz-spice-filter-active');
    syncPagerSpiceParam(activeSpiceLevels);
    if (updateUrl) setSpiceLevelsParam(activeSpiceLevels);
    // 2026-10-21 pathways: the choice is remembered (localStorage bz_spice) so it survives lane to
    // lane and visit to visit; the home page's "Welcome back" strip reads the same key. A tap
    // that clears the filter clears the memory. Clarity gets a tag so the Spice-filter share of
    // grid sessions can be measured.
    if (updateUrl) {
      try { if (activeSpiceLevels.length) localStorage.setItem('bz_spice', activeSpiceLevels.join(',')); else localStorage.removeItem('bz_spice'); } catch (_) { }
      if (window.clarity) { try { window.clarity('set', 'spice_filter', activeSpiceLevels.length ? activeSpiceLevels.join(',') : 'all'); } catch (_) { } }
    }
    markRememberedChips();
    syncLandedRow();
    return true;
  }

  function rememberedSpiceLevels() {
    try { return normalizeSpiceLevels((localStorage.getItem('bz_spice') || '').split(',')); } catch (_) { return []; }
  }
  function markRememberedChips() {
    var wrap = scope.querySelector('[data-bz-spice-filter-wrap]');
    if (!wrap) return;
    var mem = rememberedSpiceLevels();
    var opts = wrap.querySelectorAll('[data-bz-spice-filter]');
    for (var o = 0; o < opts.length; o++) {
      var on = mem.indexOf(opts[o].getAttribute('data-bz-spice-filter')) !== -1;
      opts[o].classList.toggle('is-remembered', on);
    }
  }
  // The "Just Landed" row label the Liquid puts above the new-arrival tiles only makes sense
  // while at least one of those tiles is visible under the current Spice filter.
  // 2026-10-30: a second group, Restocked recently (data-bzp-restock-row, tiles with data-bz-restock="1"
  // that are not New), on the New Arrivals and Restocks lanes. A group whose tiles the filter hides
  // loses its label; the "Everything else" label needs at least one grouped tile above it and one below.
  // 2026-11-02 (ZZ: "it should be either New or Restocked. Not just new"): on the other lanes a refill that just
  // landed sits in the top group with the new listings (its chip says Restocked), so the count names both,
  // "4 new, 2 restocked in this lane", and data-bzp-synced tells snippets/bz-tile-styles the count is right to show.
  function syncLandedRow() {
    if (!grid) return;
    var label = grid.querySelector('[data-bzp-landed-row]');
    var restockLabel = grid.querySelector('[data-bzp-restock-row]');
    var rest = grid.querySelector('[data-bzp-rest-row]');
    if (!label && !restockLabel) return;
    var tiles = gridTiles();
    var landed = 0, landedRefills = 0, restocked = 0, other = 0;
    for (var i = 0; i < tiles.length; i++) {
      if (tiles[i].hidden) continue;
      if (tiles[i].getAttribute('data-bz-landed') === '1') {
        landed += 1;
        if (tiles[i].getAttribute('data-bz-restock') === '1') landedRefills += 1;
      }
      else if (restockLabel && tiles[i].getAttribute('data-bz-restock') === '1') restocked += 1;
      else other += 1;
    }
    var spiceNote = activeSpiceLevels.length ? ' in Spice ' + activeSpiceLevels.join(', ') : '';
    if (label) {
      label.hidden = landed === 0;
      var count = label.querySelector('[data-bzp-landed-count]');
      if (count && landed) {
        var parts = [];
        if (landed - landedRefills) parts.push((landed - landedRefills) + ' new');
        if (landedRefills) parts.push(landedRefills + ' restocked');
        count.textContent = parts.join(', ') + (spiceNote || ' in this lane');
      }
      if (count) count.setAttribute('data-bzp-synced', '');
    }
    if (restockLabel) {
      restockLabel.hidden = restocked === 0;
      var rcount = restockLabel.querySelector('[data-bzp-restock-count]');
      if (rcount && restocked) rcount.textContent = restocked + ' restocked recently' + spiceNote;
    }
    if (rest) rest.hidden = (landed + restocked) === 0 || other === 0;
  }

  function toggleSpiceLevel(level) {
    level = String(level);
    var next = activeSpiceLevels.length ? activeSpiceLevels.slice() : [];
    var idx = next.indexOf(level);
    if (!next.length) {
      next = [level];
    } else if (idx === -1) {
      next.push(level);
    } else {
      next.splice(idx, 1);
    }
    applySpiceFilter(next, true);
  }

  // Sort menu: Shopify sorts navigate with ?sort_by=; spice-level sorts reorder the rendered grid.
  wireMenu(
    scope.querySelector('[data-bz-sort-wrap]'),
    '[data-bz-sort-trigger]', '[data-bz-sort-panel]', '.bz-sort__opt[data-sort], .bz-sort__opt[data-bz-local-sort]',
    function (opt, close) {
      var local = opt.getAttribute('data-bz-local-sort');
      if (local) {
        if (applySpiceSort(local, opt, true) && close) close();
        return;
      }

      var v = opt.getAttribute('data-sort');
      if (!v) return;
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', v);
      url.searchParams.delete('page');
      url.searchParams.delete('bz_sort');
      try { sessionStorage.setItem('bzCollScroll', String(window.scrollY || window.pageYOffset || 0)); } catch (_) { }
      window.location.href = url.toString();
    }
  );

  try {
    var initialLocalSort = new URL(window.location.href).searchParams.get('bz_sort');
    if (initialLocalSort) applySpiceSort(initialLocalSort, null, false);
  } catch (_) { }

  // On every page load, regardless of sort or filter, push sold-out tiles to the bottom.
  pushSoldOutToBottom();
  if (grid && window.MutationObserver) {
    new MutationObserver(pushSoldOutToBottomAsync).observe(grid, { childList: true });
  }

  // Spice filter: multi-select the rendered cards by their product tile data-spice value.
  //
  // The chips are a flat row rather than a trigger + panel, so this wires them directly instead
  // of going through wireMenu. Delegating on the wrap means updateSpiceCounts() can disable a
  // chip (a lane with no Spice 4 fellas) and the click is ignored without extra bookkeeping.
  (function wireSpiceChips() {
    var wrap = scope.querySelector('[data-bz-spice-filter-wrap]');
    if (!wrap) return;
    wrap.addEventListener('click', function (e) {
      var chip = e.target && e.target.closest ? e.target.closest('[data-bz-spice-filter], [data-bz-spice-filter-all]') : null;
      if (!chip || !wrap.contains(chip)) return;
      if (chip.disabled || chip.getAttribute('aria-disabled') === 'true') return;
      if (chip.hasAttribute('data-bz-spice-filter-all')) {
        applySpiceFilter([], true);
        return;
      }
      toggleSpiceLevel(chip.getAttribute('data-bz-spice-filter'));
    });
  })();
  updateSpiceCounts();
  // 2026-10-21 pathways: a shared or bookmarked ?bz_spice= link wins; otherwise the remembered
  // choice applies, and the URL is updated so the pager and a reload agree with the chips.
  (function applyInitialSpice() {
    var fromUrl = parseSpiceLevelsParam();
    if (fromUrl.length) { applySpiceFilter(fromUrl, false); return; }
    var mem = rememberedSpiceLevels();
    applySpiceFilter(mem, mem.length > 0);
  })();
  // 2026-11-02: a lane without the Spice chip row never reaches applySpiceFilter, so the landed row is synced here
  // too. It only reads the grid, so running it twice on the other lanes is harmless.
  syncLandedRow();

  // Lane menu: replaced the row of spice pills. Options are <a> links to real collections, so the
  // browser handles navigation and this only opens and closes the panel.
  wireMenu(
    scope.querySelector('[data-bz-lane-wrap]'),
    '[data-bz-lane-trigger]', '[data-bz-lane-panel]', '.bz-sort__opt', null
  );

  // Species menu: the options are <a> links to the species collections, so just open/close it.
  wireMenu(
    scope.querySelector('[data-bz-species-wrap]'),
    '[data-bz-species-trigger]', '[data-bz-species-panel]', '.bz-sort__opt', null
  );

  /* ---------- Show in-stock only ------------------------------------------------------------
     Sold-out fellas are not listed anywhere. Liquid cannot filter a paginated collection by
     availability, so the filter has to ride on the URL; this swaps a bare collection URL for the
     filtered one. location.replace keeps it out of the back-button history, so Back still leaves
     the collection rather than bouncing between two URLs.

     No toggle, no stored preference, no state to disagree with. Crawlers do not run this, so the
     unfiltered URL stays intact as the canonical version and sold-out products keep their internal
     links, which matters while Search Console is still working through "Discovered, not indexed". */
  (function showInStockOnly() {
    var marker = scope.querySelector('[data-bz-stock-force]');
    if (!marker) return;
    var target = marker.getAttribute('data-bz-stock-instock-url');
    if (!target) return;
    try {
      var here = new URL(window.location.href);
      // Never fight an explicit choice: a chosen sort, a page deep-link, or any active filter.
      if (here.searchParams.get('sort_by')) return;
      if (here.searchParams.get('page')) return;
      var keys = Array.prototype.slice.call(here.searchParams.keys());
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].indexOf('filter.') === 0) return;
      }
      // Carry the spice selection across the redirect. This runs before the spice filter is
      // applied, so without it a shared or bookmarked ?bz_spice= link silently lost its chips:
      // the page bounced to the in-stock URL and came back with no selection at all. Redirecting
      // is still right (sold-out fellas are not listed anywhere), so the fix is to bring the
      // parameter along rather than to stand down.
      var spice = here.searchParams.get('bz_spice');
      if (spice) {
        var withSpice = new URL(target, window.location.href);
        withSpice.searchParams.set('bz_spice', spice);
        target = withSpice.toString();
      }
    } catch (_) { return; }
    window.location.replace(target);
  })();



  // Stock menu: the options are <a> links carrying Shopify's native ?filter.v.availability=
  // URLs, so the server does the filtering and the browser handles the navigation. Nothing to
  // do here beyond opening and closing the panel.
  wireMenu(
    scope.querySelector('[data-bz-stock-wrap]'),
    '[data-bz-stock-trigger]', '[data-bz-stock-panel]', '.bz-sort__opt', null
  );

  /* ---------- Products-per-row view toggle (1 / 2 / 3 / 4 columns) ----------
     Stores the user's choice in localStorage so it persists lane to lane. "auto" (the
     unset value) keeps the responsive defaults from bz-site.css. */
  var viewBtns = scope.querySelectorAll('[data-bz-view]');
  if (viewBtns.length && grid) {
    var STORAGE_KEY = 'bzCollView';
    var MOBILE_STORAGE_KEY = 'bzCollViewMobileV2';
    var TABLET_STORAGE_KEY = 'bzCollViewTablet';
    var MOBILE_VIEW_MAX = 767;
    var mobileViewQuery = window.matchMedia ? window.matchMedia('(max-width: ' + MOBILE_VIEW_MAX + 'px)') : null;
    var viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    var activeStorageKey = viewportWidth <= MOBILE_VIEW_MAX
      ? MOBILE_STORAGE_KEY
      : (viewportWidth <= 1100 ? TABLET_STORAGE_KEY : STORAGE_KEY);
    function isMobileView() {
      if (mobileViewQuery) return mobileViewQuery.matches;
      return (window.innerWidth || document.documentElement.clientWidth || 0) <= MOBILE_VIEW_MAX;
    }
    function normalizeView(value) {
      if (value !== '1' && value !== '2' && value !== '3' && value !== '4' && value !== 'max') value = null;
      if (!isMobileView()) return value;
      return value === '1' ? '1' : '2';
    }
    function applyView(value) {
      value = normalizeView(value);
      if (value) {
        grid.setAttribute('data-bz-grid-cols', value);
      } else {
        grid.removeAttribute('data-bz-grid-cols');
      }
      for (var i = 0; i < viewBtns.length; i++) {
        var pressed = viewBtns[i].getAttribute('data-bz-view') === value;
        viewBtns[i].setAttribute('aria-pressed', pressed ? 'true' : 'false');
      }
      return value;
    }
    var saved = null;
    try { saved = localStorage.getItem(activeStorageKey); } catch (_) { }
    if (!saved) {
      // Sensible defaults so the page renders sensibly on first paint without flicker:
      // 2-up on phones, 3-up on tablets, otherwise the responsive default (bz-grid--4 etc.).
      if (viewportWidth <= MOBILE_VIEW_MAX) saved = '2';
      else if (viewportWidth <= 1100) saved = '3';
      else try { saved = localStorage.getItem(STORAGE_KEY); } catch (_) { }
    }
    applyView(saved);
    for (var j = 0; j < viewBtns.length; j++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var v = btn.getAttribute('data-bz-view');
          v = applyView(v);
          try { localStorage.setItem(activeStorageKey, v); } catch (_) { }
        });
      })(viewBtns[j]);
    }
    var onMobileViewChange = function () {
      applyView(grid.getAttribute('data-bz-grid-cols'));
    };
    if (mobileViewQuery) {
      if (mobileViewQuery.addEventListener) mobileViewQuery.addEventListener('change', onMobileViewChange);
      else if (mobileViewQuery.addListener) mobileViewQuery.addListener(onMobileViewChange);
    }
  }
})();
